/** Generated by AI - Edit by prompting */

import { getProjectAddresses } from '@bulthaup/api/entities/project';
import {
  Badge,
  Card,
  Divider,
  Grid,
  Group,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import {
  IconBuilding,
  IconHome,
  IconMail,
  IconMapPin,
  IconPhone,
  IconTruck,
  IconUser,
} from '@tabler/icons-react';

import { RouterOutput } from '../../../../../../services/trpc';
import { label } from '../../../../../../utils/label';

export function ClientSection({
  project,
}: {
  project: RouterOutput['projects']['get'];
}) {
  const { customer } = project;
  const addresses = getProjectAddresses({
    isDeliveryAddressOverride: project.isDeliveryAddressOverride,
    isInstallationAddressOverride: project.isInstallationAddressOverride,
    isBillingAddressOverride: project.isBillingAddressOverride,
    homeAddress: project.homeAddress,
    deliveryAddress: project.deliveryAddress,
    installationAddress: project.installationAddress,
    billingAddress: project.billingAddress,
  });

  const formatAddress = (address: typeof project.homeAddress) => {
    const parts = [
      address.address1,
      address.address2,
      address.address3,
      [address.postalCode, address.city].filter(Boolean).join(' '),
    ].filter(Boolean);

    return parts.length > 0 ? parts : null;
  };

  const InfoRow = ({
    icon,
    label: labelText,
    value,
  }: {
    icon: React.ReactNode;
    label: string;
    value: string | null | undefined;
  }) => {
    if (!value) return null;
    return (
      <Group gap="xs" wrap="nowrap">
        {icon}
        <Stack gap={0}>
          <Text size="xs" c="dimmed">
            {labelText}
          </Text>
          <Text size="sm">{value}</Text>
        </Stack>
      </Group>
    );
  };

  const AddressCard = ({
    titles,
    icons,
    address,
    showAccessDetails = false,
  }: {
    titles: string[];
    icons: React.ReactNode[];
    address: typeof project.homeAddress;
    showAccessDetails?: boolean;
  }) => {
    const addressLines = formatAddress(address);
    if (!addressLines) return null;

    // If all 4 addresses are the same, show just "Adresse" with home icon
    const isUniqueAddress = titles.length === 4;

    return (
      <Card withBorder p="md">
        <Stack gap="sm">
          {isUniqueAddress ? (
            <Group gap="xs">
              <IconHome size={18} />
              <Title order={5}>Domicile</Title>
            </Group>
          ) : (
            <Group gap="xs" wrap="wrap">
              {icons.map((icon, idx) => (
                <Group key={idx} gap={4}>
                  {icon}
                  <Title order={5}>{titles[idx]}</Title>
                  {idx < titles.length - 1 && <Text c="dimmed">+</Text>}
                </Group>
              ))}
            </Group>
          )}
          <Stack gap={4}>
            {addressLines.map((line, idx) => (
              <Text key={idx} size="sm">
                {line}
              </Text>
            ))}
          </Stack>
          {showAccessDetails &&
            (address.code1 ||
              address.code2 ||
              address.floor ||
              address.elevator) && (
              <>
                <Divider />
                <Grid gutter="xs">
                  {address.code1 && (
                    <Grid.Col span={6}>
                      <Text size="xs" c="dimmed">
                        Code 1
                      </Text>
                      <Text size="sm">{address.code1}</Text>
                    </Grid.Col>
                  )}
                  {address.code2 && (
                    <Grid.Col span={6}>
                      <Text size="xs" c="dimmed">
                        Code 2
                      </Text>
                      <Text size="sm">{address.code2}</Text>
                    </Grid.Col>
                  )}
                  {address.floor && (
                    <Grid.Col span={6}>
                      <Text size="xs" c="dimmed">
                        Étage
                      </Text>
                      <Text size="sm">{address.floor}</Text>
                    </Grid.Col>
                  )}
                  {address.elevator && (
                    <Grid.Col span={6}>
                      <Text size="xs" c="dimmed">
                        Ascenseur
                      </Text>
                      <Text size="sm">{address.elevator}</Text>
                    </Grid.Col>
                  )}
                </Grid>
              </>
            )}
        </Stack>
      </Card>
    );
  };

  // Group identical addresses
  const addressGroups = (() => {
    const addressTypes = [
      {
        key: 'homeAddress',
        title: 'Domicile',
        icon: <IconHome size={18} />,
        showAccessDetails: true,
      },
      {
        key: 'deliveryAddress',
        title: 'Livraison',
        icon: <IconTruck size={18} />,
        showAccessDetails: true,
      },
      {
        key: 'installationAddress',
        title: 'Installation',
        icon: <IconMapPin size={18} />,
        showAccessDetails: true,
      },
      {
        key: 'billingAddress',
        title: 'Facturation',
        icon: <IconBuilding size={18} />,
        showAccessDetails: false,
      },
    ];

    const groups: Array<{
      address: typeof project.homeAddress;
      titles: string[];
      icons: React.ReactNode[];
      showAccessDetails: boolean;
    }> = [];

    const addressToString = (addr: typeof project.homeAddress) =>
      JSON.stringify({
        address1: addr.address1,
        address2: addr.address2,
        address3: addr.address3,
        city: addr.city,
        postalCode: addr.postalCode,
        code1: addr.code1,
        code2: addr.code2,
        floor: addr.floor,
        elevator: addr.elevator,
      });

    addressTypes.forEach(type => {
      const address = addresses[type.key as keyof typeof addresses];
      const addressStr = addressToString(address);

      const existingGroup = groups.find(
        g => addressToString(g.address) === addressStr,
      );

      if (existingGroup) {
        existingGroup.titles.push(type.title);
        existingGroup.icons.push(type.icon);
        existingGroup.showAccessDetails =
          existingGroup.showAccessDetails || type.showAccessDetails;
      } else {
        groups.push({
          address,
          titles: [type.title],
          icons: [type.icon],
          showAccessDetails: type.showAccessDetails,
        });
      }
    });

    return groups;
  })();

  return (
    <Stack gap="lg">
      {/* Main Client Info */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Group justify="space-between">
            <Title order={3}>Informations client</Title>
            {customer.signer && (
              <Badge size="lg" variant="light">
                Signataire: {label(customer.signer)}
              </Badge>
            )}
          </Group>

          {customer.companyName && (
            <Group gap="xs">
              <IconBuilding size={20} />
              <Stack gap={0}>
                <Text size="xs" c="dimmed">
                  Entreprise
                </Text>
                <Text size="lg" fw={600}>
                  {customer.companyName}
                </Text>
              </Stack>
            </Group>
          )}

          <Grid gutter="lg">
            {/* Client 1 */}
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Card withBorder p="md" bg="gray.0">
                <Stack gap="sm">
                  <Group gap="xs">
                    <IconUser size={18} />
                    <Title order={5}>Client 1</Title>
                  </Group>
                  <Text size="md" fw={500}>
                    {customer.customer1Title} {customer.customer1FirstName}{' '}
                    {customer.customer1LastName}
                  </Text>
                  <Stack gap="xs">
                    <InfoRow
                      icon={<IconMail size={16} />}
                      label="Email"
                      value={customer.customer1Email}
                    />
                    <InfoRow
                      icon={<IconPhone size={16} />}
                      label="Téléphone"
                      value={customer.customer1Phone}
                    />
                  </Stack>
                </Stack>
              </Card>
            </Grid.Col>

            {/* Client 2 */}
            {customer.customer2FirstName && (
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Card withBorder p="md" bg="gray.0">
                  <Stack gap="sm">
                    <Group gap="xs">
                      <IconUser size={18} />
                      <Title order={5}>Client 2</Title>
                    </Group>
                    <Text size="md" fw={500}>
                      {customer.customer2Title} {customer.customer2FirstName}{' '}
                      {customer.customer2LastName}
                    </Text>
                    <Stack gap="xs">
                      <InfoRow
                        icon={<IconMail size={16} />}
                        label="Email"
                        value={customer.customer2Email}
                      />
                      <InfoRow
                        icon={<IconPhone size={16} />}
                        label="Téléphone"
                        value={customer.customer2Phone}
                      />
                    </Stack>
                  </Stack>
                </Card>
              </Grid.Col>
            )}
          </Grid>
        </Stack>
      </Card>

      {/* Contacts */}
      {customer.contacts && customer.contacts.length > 0 && (
        <Card withBorder p="lg">
          <Stack gap="md">
            <Title order={4}>Contacts</Title>
            <Grid gutter="md">
              {customer.contacts.map((contact, idx) => (
                <Grid.Col key={contact.id || idx} span={{ base: 12, md: 6 }}>
                  <Card withBorder p="md" bg="gray.0">
                    <Stack gap="sm">
                      <Group justify="space-between">
                        <Badge variant="filled">{contact.type}</Badge>
                      </Group>
                      <Text size="md" fw={500}>
                        {contact.firstName} {contact.lastName}
                      </Text>
                      <Stack gap="xs">
                        <InfoRow
                          icon={<IconMail size={16} />}
                          label="Email"
                          value={contact.email}
                        />
                        <InfoRow
                          icon={<IconPhone size={16} />}
                          label="Téléphone"
                          value={contact.phone}
                        />
                      </Stack>
                      {(contact.quoteNotificationEnabled ||
                        contact.technicalPlanNotificationEnabled ||
                        contact.constructionSiteReviewNotificationEnabled ||
                        contact.constructionSiteReportNotificationEnabled) && (
                        <>
                          <Divider />
                          <Stack gap={4}>
                            <Text size="xs" c="dimmed" fw={600}>
                              Notifications
                            </Text>
                            <Group gap="xs">
                              {contact.quoteNotificationEnabled && (
                                <Badge size="xs" variant="light">
                                  Devis
                                </Badge>
                              )}
                              {contact.technicalPlanNotificationEnabled && (
                                <Badge size="xs" variant="light">
                                  Plan technique
                                </Badge>
                              )}
                              {contact.constructionSiteReviewNotificationEnabled && (
                                <Badge size="xs" variant="light">
                                  CR chantier
                                </Badge>
                              )}
                              {contact.constructionSiteReportNotificationEnabled && (
                                <Badge size="xs" variant="light">
                                  PV chantier
                                </Badge>
                              )}
                            </Group>
                          </Stack>
                        </>
                      )}
                    </Stack>
                  </Card>
                </Grid.Col>
              ))}
            </Grid>
          </Stack>
        </Card>
      )}

      {/* Addresses */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Adresses</Title>
          <Grid gutter="md">
            {addressGroups.map((group, idx) => (
              <Grid.Col key={idx} span={{ base: 12, md: 6 }}>
                <AddressCard
                  titles={group.titles}
                  icons={group.icons}
                  address={group.address}
                  showAccessDetails={group.showAccessDetails}
                />
              </Grid.Col>
            ))}
          </Grid>
        </Stack>
      </Card>
    </Stack>
  );
}
